import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useAlbum } from '../hooks/useAlbum'
import { useDomain } from '../hooks/useDomain'
import { useConfig } from '../hooks/useConfig'
import { usePageMeta } from '../hooks/usePageMeta'
import { LinkType } from '../types'
import { useTracking } from '../hooks/useTracking'
import { CONTENT_ORIGIN } from '../config'

/* ---------------- styles ---------------- */
const Page = styled.main`
    display: grid;
    place-items: center;
    height: 100vh; /* lock to viewport */
    width: 100vw;
    padding: 24px;
    box-sizing: border-box;
    overflow: hidden; /* prevent internal scroll */
`

/* Fullscreen mask to visually cover the footer */
const ScreenMask = styled.div`
    position: fixed;
    inset: 0;
    background: ${(p) => p.theme.background};
    z-index: 3; /* higher than footer */
    pointer-events: none; /* don't block interactions */
`

const Card = styled.section`
    width: min(900px, 94vw);
    position: relative;
    z-index: 4; /* game card above the mask */
    border-radius: 24px;
    padding: 28px;
    border: 2px solid transparent;
    background:
        linear-gradient(
                180deg,
                rgba(255, 255, 255, 0.06),
                rgba(255, 255, 255, 0.02)
            )
            padding-box,
        conic-gradient(
                from 120deg,
                #ff7a7a33,
                #ffd36a33,
                #78ff9333,
                #6ae0ff33,
                #b28dff33,
                #ff7a7a33
            )
            border-box;
    box-shadow:
        0 10px 30px rgba(0, 0, 0, 0.35),
        inset 0 0 0 1px rgba(255, 255, 255, 0.06);
    overflow: hidden;
    backdrop-filter: blur(6px);
`

const H1 = styled.h1`
    margin: 0 0 6px;
    font-size: clamp(28px, 4vw, 44px);
    letter-spacing: 0.4px;
`
const Sub = styled.p`
    margin: 0 0 16px;
    color: ${(p) => p.theme.secondaryText || '#9aa0aa'};
    font-size: 14px;
`
const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 20px;
    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
`
const Panel = styled.section`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 18px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`
const HUD = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`
const Badge = styled.span`
    display: inline-block;
    padding: 6px 10px;
    border-radius: 999px;
    background: #2a2a33;
    color: #e8ebf0;
    font-weight: 600;
    font-size: 12px;
`
const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 999px;
    background: #223022;
    color: #9defb3;
    font-weight: 700;
    font-size: 12px;
`
const Button = styled.button<{ $ghost?: boolean }>`
    background: ${(p) => (p.$ghost ? '#2a2a33' : '#22c55e')};
    color: ${(p) => (p.$ghost ? '#e8ebf0' : '#0b1510')};
    border: 0;
    padding: 12px 14px;
    border-radius: 12px;
    font-weight: 700;
    cursor: pointer;
`
const Reveal = styled.section<{ $visible: boolean }>`
    margin-top: 18px;
    display: ${(p) => (p.$visible ? 'flex' : 'none')};
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 18px;
    align-items: center;
    gap: 12px;
`
const Actions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
`
const Kbd = styled.kbd`
    font-family:
        ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono',
        monospace;
    background: #25252b;
    padding: 2px 6px;
    border-radius: 6px;
    border: 1px solid #2f2f39;
`
const Cover = styled.img`
    width: 72px;
    height: 72px;
    border-radius: 12px;
    object-fit: cover;
    margin-right: 10px;
    border: 1px solid rgba(255, 255, 255, 0.08);
`
const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`

/* ---------------- game core ---------------- */
type Point = { x: number; y: number }
const CELL = 20
const GRID = 20
const GOAL = 7
const BOARD_PX = CELL * GRID

const drawBoard = (
    ctx: CanvasRenderingContext2D,
    snake: Point[],
    food: Point
) => {
    ctx.fillStyle = '#0e1117'
    ctx.fillRect(0, 0, BOARD_PX, BOARD_PX)
    ctx.strokeStyle = '#171a20'
    ctx.lineWidth = 1
    for (let i = 1; i < GRID; i++) {
        ctx.beginPath()
        ctx.moveTo(i * CELL, 0)
        ctx.lineTo(i * CELL, BOARD_PX)
        ctx.stroke()
        ctx.beginPath()
        ctx.moveTo(0, i * CELL)
        ctx.lineTo(BOARD_PX, i * CELL)
        ctx.stroke()
    }
    ctx.fillStyle = '#ef4444'
    ctx.fillRect(food.x * CELL + 2, food.y * CELL + 2, CELL - 4, CELL - 4)
    for (let i = 0; i < snake.length; i++) {
        ctx.fillStyle = i === 0 ? '#22c55e' : '#38f2a6'
        ctx.fillRect(
            snake[i].x * CELL + 1,
            snake[i].y * CELL + 1,
            CELL - 2,
            CELL - 2
        )
    }
}

const RevealGame: React.FC = () => {
    const { album } = useAlbum()
    const domain = useDomain()
    const { config } = useConfig(domain)
    const trackLink = useTracking()

    usePageMeta(
        config && album && `${config?.name} | ${album?.title} — Snake`,
        config &&
            album &&
            `${album?.title} - ${album?.artists?.map((a: any) => a.name).join(', ') || ''}`
    )

    const spotifyLink: LinkType | undefined = useMemo(() => {
        const link = (album?.links || []).find(
            (l: LinkType) => (l.platform || '').toLowerCase() === 'spotify'
        )
        return link
    }, [album])

    /* disable global scroll while game is mounted */
    useEffect(() => {
        const body = document.body
        const html = document.documentElement
        const prevBodyOverflow = body.style.overflow
        const prevHtmlOverflow = html.style.overflow
        body.style.overflow = 'hidden'
        html.style.overflow = 'hidden'
        return () => {
            body.style.overflow = prevBodyOverflow
            html.style.overflow = prevHtmlOverflow
        }
    }, [])

    /* refs/state */
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const [score, setScore] = useState(0)
    const [best, setBest] = useState(0)
    const [speed, setSpeed] = useState(140)
    const [status, setStatus] = useState('Drücke Start, um zu spielen.')
    const [revealed, setRevealed] = useState(false)
    const runningRef = useRef(false)

    const bestKey = useMemo(
        () => `snake_best_${album?.id || 'global'}`,
        [album?.id]
    )

    const snakeRef = useRef<Point[]>([
        { x: 8, y: 10 },
        { x: 7, y: 10 },
        { x: 6, y: 10 },
    ])
    const dirRef = useRef<Point>({ x: 1, y: 0 })
    const nextDirRef = useRef<Point>({ x: 1, y: 0 })
    const foodRef = useRef<Point>({ x: 0, y: 0 })

    const placeFood = () => {
        while (true) {
            const f = {
                x: Math.floor(Math.random() * GRID),
                y: Math.floor(Math.random() * GRID),
            }
            if (!snakeRef.current.some((s) => s.x === f.x && s.y === f.y)) {
                foodRef.current = f
                return
            }
        }
    }

    /* speed ref */
    const speedRef = useRef(speed)
    useEffect(() => {
        speedRef.current = speed
    }, [speed])

    /* load best when album changes */
    useEffect(() => {
        const storedBest = Number(localStorage.getItem(bestKey) || 0)
        setBest(storedBest)
    }, [bestKey])

    /* key handlers (once) */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const k = e.key.toLowerCase()
            if (k === ' ') e.preventDefault()
            if (k === 'arrowup' || k === 'w') setDir(0, -1)
            else if (k === 'arrowdown' || k === 's') setDir(0, 1)
            else if (k === 'arrowleft' || k === 'a') setDir(-1, 0)
            else if (k === 'arrowright' || k === 'd') setDir(1, 0)
            else if (k === ' ') {
                timerRef.current ? pause() : start()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [])

    /* INIT canvas once */
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctxRef.current = ctx

        snakeRef.current = [
            { x: 8, y: 10 },
            { x: 7, y: 10 },
            { x: 6, y: 10 },
        ]
        dirRef.current = { x: 1, y: 0 }
        nextDirRef.current = { x: 1, y: 0 }
        runningRef.current = false
        setScore(0)
        setSpeed(140)
        setStatus('Drücke Start, um zu spielen.')
        setRevealed(false)
        placeFood()
        drawBoard(ctx, snakeRef.current, foodRef.current)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            ctxRef.current = null
        }
    }, [])

    /* actions */
    const reset = () => {
        snakeRef.current = [
            { x: 8, y: 10 },
            { x: 7, y: 10 },
            { x: 6, y: 10 },
        ]
        dirRef.current = { x: 1, y: 0 }
        nextDirRef.current = { x: 1, y: 0 }
        runningRef.current = false
        setScore(0)
        setSpeed(140)
        setStatus('Drücke Start, um zu spielen.')
        setRevealed(false)
        placeFood()
        if (ctxRef.current)
            drawBoard(ctxRef.current, snakeRef.current, foodRef.current)
    }

    const start = () => {
        if (timerRef.current) return
        if (!ctxRef.current) return
        if (snakeRef.current.length === 0) reset()
        dirRef.current = { x: 1, y: 0 }
        nextDirRef.current = { x: 1, y: 0 }
        runningRef.current = true
        setStatus('Viel Glück!')
        timerRef.current = setInterval(tick, speedRef.current)
    }

    const pause = () => {
        if (!timerRef.current) return
        clearInterval(timerRef.current)
        timerRef.current = null
        runningRef.current = false
        setStatus('Pausiert')
    }

    const restartTimer = (newSpeed: number) => {
        if (!timerRef.current) return
        clearInterval(timerRef.current)
        timerRef.current = setInterval(tick, newSpeed)
    }

    const gameOver = () => {
        pause()
        runningRef.current = false
        const newBest = Math.max(best, score)
        localStorage.setItem(bestKey, String(newBest))
        setBest(newBest)
        setStatus('💥 Spiel vorbei — drücke Start für einen neuen Versuch.')
    }

    const youWin = () => {
        pause()
        runningRef.current = false
        const newBest = Math.max(best, score)
        localStorage.setItem(bestKey, String(newBest))
        setBest(newBest)
        setStatus('🎉 Gewonnen! Link unten freigeschaltet.')
        setRevealed(true)
    }

    const setDir = (nx: number, ny: number) => {
        const d = runningRef.current
            ? dirRef.current
            : {
                  x: snakeRef.current[0].x - snakeRef.current[1].x,
                  y: snakeRef.current[0].y - snakeRef.current[1].y,
              }
        if (nx === -d.x && ny === -d.y) return
        nextDirRef.current = { x: nx, y: ny }
    }

    const tick = () => {
        if (snakeRef.current.length === 0) return

        dirRef.current = nextDirRef.current
        const newHead = {
            x: snakeRef.current[0].x + dirRef.current.x,
            y: snakeRef.current[0].y + dirRef.current.y,
        }

        if (
            newHead.x < 0 ||
            newHead.y < 0 ||
            newHead.x >= GRID ||
            newHead.y >= GRID
        ) {
            gameOver()
            return
        }

        const willEat =
            newHead.x === foodRef.current.x && newHead.y === foodRef.current.y

        const nextSnake = willEat
            ? [newHead, ...snakeRef.current]
            : [newHead, ...snakeRef.current.slice(0, -1)]

        const hitSelf = nextSnake
            .slice(1)
            .some((p) => p.x === newHead.x && p.y === newHead.y)
        if (hitSelf) {
            gameOver()
            return
        }

        snakeRef.current = nextSnake

        if (willEat) {
            setScore((s) => {
                const n = s + 1
                if (n % 3 === 0 && speedRef.current > 80) {
                    const newSpeed = speedRef.current - 10
                    speedRef.current = newSpeed
                    setSpeed(newSpeed)
                    restartTimer(newSpeed)
                }
                if (n >= GOAL) youWin()
                return n
            })
            placeFood()
        }

        if (ctxRef.current)
            drawBoard(ctxRef.current, snakeRef.current, foodRef.current)
    }

    /* UI */
    const coverUrl = `${CONTENT_ORIGIN}${
        album?.cover?.formats?.small?.url ||
        album?.cover?.url ||
        '/images/default_cover.jpg'
    }`

    const handleTrackOpen = () => {
        if (spotifyLink) trackLink(spotifyLink.documentId, domain)
    }

    return (
        <Page aria-labelledby="title">
            <ScreenMask /> {/* cover footer / anything behind */}
            <Card>
                <HeaderRow>
                    <Cover
                        src={coverUrl}
                        alt={`Cover art of ${album?.title || 'Album'}`}
                    />
                    <div>
                        <H1 id="title">
                            Snake freischalten — {album?.title ?? '—'}
                        </H1>
                        <Sub>
                            Iss <strong>{GOAL}</strong> Äpfel ohne zu crashen,
                            um den Spotify-Link freizuschalten. Benutze{' '}
                            <Kbd>W/A/S/D</Kbd> oder die Pfeiltasten. Drücke{' '}
                            <Kbd>Leertaste</Kbd> zum Pausieren.
                        </Sub>
                    </div>
                </HeaderRow>

                <Grid>
                    <Panel aria-label="Snake Spiel">
                        <HUD>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 12,
                                    alignItems: 'center',
                                }}
                            >
                                <Badge>Punkte: {score}</Badge>
                                <Badge>Bestwert: {best}</Badge>
                                <Badge>
                                    Geschwindigkeit: {(140 / speed).toFixed(1)}x
                                </Badge>
                            </div>
                            <div>
                                <Button type="button" onClick={start}>
                                    Start
                                </Button>{' '}
                                <Button
                                    $ghost
                                    type="button"
                                    onClick={() =>
                                        timerRef.current ? pause() : start()
                                    }
                                >
                                    {timerRef.current ? 'Pause' : 'Fortsetzen'}
                                </Button>{' '}
                                <Button
                                    $ghost
                                    type="button"
                                    onClick={() => {
                                        pause()
                                        reset()
                                    }}
                                >
                                    Neustart
                                </Button>
                            </div>
                        </HUD>

                        <canvas
                            ref={canvasRef}
                            width={BOARD_PX}
                            height={BOARD_PX}
                            aria-label="Snake Spielfeld"
                            role="img"
                            style={{
                                display: 'block',
                                width: '100%',
                                height: 'auto',
                                imageRendering: 'pixelated' as const,
                                background: '#111318',
                                borderRadius: 12,
                            }}
                        />

                        <Sub aria-live="polite">{status}</Sub>
                    </Panel>

                    <Panel aria-label="Spielanleitung">
                        <Pill aria-hidden="true">Spielregeln</Pill>
                        <ul>
                            <li>Iss Äpfel, um zu wachsen.</li>
                            <li>Vermeide Wände und deinen eigenen Schwanz.</li>
                            <li>
                                Erreiche das Ziel, um den Link freizuschalten.
                            </li>
                        </ul>

                        <Pill aria-hidden="true">Steuerung</Pill>
                        <ul>
                            <li>
                                Pfeiltasten oder <Kbd>W/A/S/D</Kbd> zum Bewegen.
                            </li>
                            <li>
                                <Kbd>Leertaste</Kbd> zum Pausieren/Fortsetzen.
                            </li>
                            <li>Buttons: Start / Pause / Neustart.</li>
                        </ul>

                        <Pill aria-hidden="true">Hinweis</Pill>
                        <Sub>
                            Dies ist nur zum Spaß. Der Spotify-Link wird erst
                            nach einem Sieg angezeigt.
                        </Sub>

                        <Reveal $visible={revealed} aria-hidden={!revealed}>
                            <Pill aria-hidden="true">Freigeschaltet</Pill>
                            <div style={{ flex: 1, minWidth: 200 }}>
                                {spotifyLink ? (
                                    <>
                                        <div
                                            style={{
                                                fontSize: 16,
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {spotifyLink.url}
                                        </div>
                                        <Actions>
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    handleTrackOpen()
                                                    window.open(
                                                        spotifyLink.url,
                                                        '_blank',
                                                        'noopener'
                                                    )
                                                }}
                                            >
                                                In Spotify öffnen
                                            </Button>
                                            <Button
                                                $ghost
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(
                                                            spotifyLink.url
                                                        )
                                                        setStatus(
                                                            '✅ Link kopiert!'
                                                        )
                                                        setTimeout(
                                                            () => setStatus(''),
                                                            1200
                                                        )
                                                    } catch {
                                                        alert(
                                                            'Kopieren fehlgeschlagen.'
                                                        )
                                                    }
                                                }}
                                            >
                                                Link kopieren
                                            </Button>
                                        </Actions>
                                    </>
                                ) : (
                                    <div style={{ fontSize: 16 }}>
                                        Kein Spotify-Link im Release gefunden.
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    </Panel>
                </Grid>
            </Card>
        </Page>
    )
}

export default RevealGame
